import { useEffect, useMemo, useState } from "react";
import { Inbox, Megaphone, Users } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectItem,
  MultiSelectTrigger,
  MultiSelectValue,
} from "@/components/ui/library/multi-select";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useDebounce } from "@/hooks/useDebounce";
import { useAlertAudiencePreview, useCreateAlert } from "../api/useAlerts";
import type { AlertAudience, AlertComposeOptions, AlertMode } from "../types";

const TITLE_MAX = 120;
const MESSAGE_MAX = 4000;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  options: AlertComposeOptions;
}

/**
 * "New alert" — title, message, how to deliver it (silent box row vs popup)
 * and who gets it: the roles under the sender, narrowed by branch and by the
 * custom-field options people were given access to on their user settings.
 * "Will reach N people" is the server's own count of the materialised
 * audience, so what it says is exactly who gets it.
 */
export default function AlertComposeDialog({ open, onOpenChange, options }: Props) {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [mode, setMode] = useState<AlertMode>("SILENT");
  const [roleIds, setRoleIds] = useState<number[]>([]);
  const [branchIds, setBranchIds] = useState<number[]>([]);
  const [cfByField, setCfByField] = useState<Record<number, number[]>>({});

  const create = useCreateAlert();

  // Fresh form each time it opens.
  useEffect(() => {
    if (!open) return;
    setTitle("");
    setMessage("");
    setMode("SILENT");
    setRoleIds([]);
    setBranchIds([]);
    setCfByField({});
  }, [open]);

  const audience = useMemo<AlertAudience>(
    () => ({
      roleIds,
      branchIds,
      cfItemIds: Object.values(cfByField).flat(),
    }),
    [roleIds, branchIds, cfByField],
  );
  const debouncedAudience = useDebounce(audience, 350);
  const preview = useAlertAudiencePreview(debouncedAudience, open);

  const count = preview.data?.count ?? null;
  const canSend =
    title.trim().length > 0 &&
    message.trim().length > 0 &&
    (count ?? 0) > 0 &&
    !create.isPending;

  const send = async () => {
    try {
      const alert = await create.mutateAsync({
        title: title.trim(),
        message: message.trim(),
        mode,
        ...audience,
      });
      toast.success(
        `Alert ${mode === "ANNOUNCE" ? "announced" : "sent"} to ${alert?.recipientCount ?? count ?? 0} people`,
      );
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Could not send the alert");
    }
  };

  const everyoneLabel = options.scope === "everyone" ? "Everyone" : "All roles under you";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* minmax(0,1fr): the dialog is a grid, and its one column otherwise grows to its longest unwrapped line
          (the "Will reach … · names" footer), pushing every field past the right edge and cutting off Announce. */}
      <DialogContent className="sm:max-w-[560px] grid-cols-[minmax(0,1fr)] rounded-xl p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-[var(--color-black-1)]">
          <DialogTitle className="flex items-center gap-2 text-base font-semibold text-[#051912]">
            <span className="flex size-8 items-center justify-center rounded-full bg-[var(--color-green-1)]">
              <Megaphone className="size-4 text-[var(--color-primary)]" />
            </span>
            New alert
          </DialogTitle>
          <DialogDescription className="text-[12px] text-[#697571]">
            A short notice for your team. It lands in their Alert box; announce it to pop it up on
            their screens too.
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 py-4 flex flex-col gap-4 max-h-[65vh] overflow-y-auto">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="alert-title">Title</Label>
            <Input
              id="alert-title"
              value={title}
              maxLength={TITLE_MAX}
              placeholder="e.g. Office closed tomorrow"
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="alert-message">Message</Label>
              <span className="text-[10.5px] text-[var(--color-black-4)]">
                {message.length}/{MESSAGE_MAX}
              </span>
            </div>
            <Textarea
              id="alert-message"
              value={message}
              maxLength={MESSAGE_MAX}
              rows={4}
              placeholder="What should they know?"
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>How to deliver</Label>
            <div className="grid grid-cols-2 gap-2">
              <ModeCard
                active={mode === "SILENT"}
                onClick={() => setMode("SILENT")}
                icon={<Inbox className="size-4" />}
                title="Silent"
                text="Adds to their Alert box, with a corner notice like a chat message."
              />
              <ModeCard
                active={mode === "ANNOUNCE"}
                onClick={() => setMode("ANNOUNCE")}
                icon={<Megaphone className="size-4" />}
                title="Announce"
                text="Pops up on everyone's screen now, and on login until they've seen it."
                tone="orange"
              />
            </div>
          </div>

          <div className="flex flex-col gap-2.5">
            <Label className="flex items-center gap-1.5">
              <Users className="size-3.5" />
              Who gets it
            </Label>

            <AudiencePicker
              label="Roles"
              placeholder={everyoneLabel}
              value={roleIds}
              onChange={setRoleIds}
              options={options.roles.map((r) => ({
                id: r.id,
                label: `${r.name} (${r.userCount})`,
              }))}
            />

            {options.branches.length > 0 && (
              <AudiencePicker
                label="Branches"
                placeholder="All branches"
                value={branchIds}
                onChange={setBranchIds}
                options={options.branches.map((b) => ({ id: b.id, label: b.name }))}
              />
            )}

            {options.fields.map((f) => (
              <AudiencePicker
                key={f.id}
                label={f.name}
                placeholder={`Any ${f.name}`}
                value={cfByField[f.id] ?? []}
                onChange={(ids) =>
                  setCfByField((prev) => {
                    const next = { ...prev };
                    if (ids.length === 0) delete next[f.id];
                    else next[f.id] = ids;
                    return next;
                  })
                }
                options={f.options.map((o) => ({ id: o.id, label: o.value }))}
              />
            ))}

            {options.fields.length > 0 && (
              <p className="m-0 text-[10.5px] text-[var(--color-black-4)]">
                A field matches the options each person was given access to in their user
                settings.
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="px-5 py-3 border-t border-[var(--color-black-1)] flex items-center justify-between gap-3 sm:justify-between">
          <div className="min-w-0 text-[11.5px] text-[var(--color-black-6)]">
            {preview.isLoading && count == null ? (
              <span className="animate-pulse">Counting people…</span>
            ) : count === 0 ? (
              <span className="text-red-500">Nobody matches this audience yet</span>
            ) : count != null ? (
              <span className="block truncate">
                Will reach{" "}
                <span className="font-semibold text-[var(--color-black-9)]">
                  {count} {count === 1 ? "person" : "people"}
                </span>
                {preview.data?.sample.length ? (
                  <span className="text-[var(--color-black-4)]">
                    {" "}
                    · {preview.data.sample.join(", ")}
                    {count > preview.data.sample.length ? ` +${count - preview.data.sample.length}` : ""}
                  </span>
                ) : null}
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={create.isPending}>
              Cancel
            </Button>
            <Button variant="primary" onClick={send} disabled={!canSend}>
              {create.isPending ? "Sending…" : mode === "ANNOUNCE" ? "Announce" : "Send alert"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ModeCard({
  active,
  onClick,
  icon,
  title,
  text,
  tone = "green",
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  text: string;
  tone?: "green" | "orange";
}) {
  const ring = tone === "orange" ? "border-orange-400 bg-orange-50/60" : "border-[var(--color-primary)] bg-[var(--color-green-1)]/60";
  const chip = tone === "orange" ? "bg-orange-100 text-orange-600" : "bg-[var(--color-green-1)] text-[var(--color-primary)]";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "flex items-start gap-2.5 rounded-xl border p-3 text-left transition-colors",
        active ? ring : "border-[var(--color-black-2)] hover:bg-[var(--color-black-1)]/60",
      )}
    >
      <span className={cn("mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full", chip)}>
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block text-[12.5px] font-semibold text-[var(--color-black-9)]">{title}</span>
        <span className="block mt-0.5 text-[11px] leading-snug text-[var(--color-black-6)]">{text}</span>
      </span>
    </button>
  );
}

function AudiencePicker({
  label,
  placeholder,
  value,
  onChange,
  options,
}: {
  label: string;
  placeholder: string;
  value: number[];
  onChange: (ids: number[]) => void;
  options: Array<{ id: number; label: string }>;
}) {
  return (
    <div className="grid grid-cols-[88px_minmax(0,1fr)] items-center gap-2">
      <span className="text-[11.5px] text-[var(--color-black-6)] truncate" title={label}>
        {label}
      </span>
      <MultiSelect
        value={value.map(String)}
        onValueChange={(vals) => onChange(vals.map(Number).filter((n) => Number.isFinite(n)))}
      >
        <MultiSelectTrigger className="w-full" size="sm">
          <MultiSelectValue
            placeholder={placeholder}
            options={options.map((o) => ({ value: String(o.id), label: o.label }))}
          />
        </MultiSelectTrigger>
        <MultiSelectContent searchPlaceholder={`Search ${label.toLowerCase()}…`} className="z-[300]">
          {options.map((o) => (
            <MultiSelectItem key={o.id} value={String(o.id)}>
              {o.label}
            </MultiSelectItem>
          ))}
        </MultiSelectContent>
      </MultiSelect>
    </div>
  );
}
